import { BuildADashTrackingEvents, emitMixpanelUserEvent } from '@revelio/core';
import { validateReportDocumentLayout } from '@revelio/core/report-document-layout';

import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import {
  blockItems,
  MAX_REPORT_FLOW_COLUMNS,
} from '../components/ReportFlow/report-flow-reducer';
import type { ReportFlowPage } from '../components/ReportFlow/report-flow-types';
import type { ReportDocumentConfig } from './report-document-types';

const FALLBACK_PAGE_ID = 'page-1';

// A load repair is not written back, so every refetch would report it again.
const reportedLoadRepairs = new Set<string>();

const isSoundBlock = (block: ReportFlowPage['blocks'][number]) =>
  block.kind === 'item' ||
  (block.items.length >= 2 && block.items.length <= MAX_REPORT_FLOW_COLUMNS);

/** Rows of up to the column limit; a lone item stands on its own. */
const toRows = (items: PositionedItem[]): ReportFlowPage['blocks'] => {
  const rows: ReportFlowPage['blocks'] = [];
  for (let start = 0; start < items.length; start += MAX_REPORT_FLOW_COLUMNS) {
    const row = items.slice(start, start + MAX_REPORT_FLOW_COLUMNS);
    const [first] = row;
    if (first === undefined) continue;
    rows.push(
      row.length === 1
        ? { kind: 'item', item: first }
        : { kind: 'columns', items: row }
    );
  }
  return rows;
};

/** Keeps the first placement of an item placed twice, keeps columns within 2 to 3, renames repeated page ids and guarantees one page. */
export const reconcileReportDocument = (
  document: ReportDocumentConfig,
  stage: 'load' | 'save'
): ReportDocumentConfig => {
  const seen = new Set<string>();
  const pageIds = new Set(document.pages.map((page) => page.id));
  const seenPageIds = new Set<string>();
  let changed = false;
  const pages: ReportFlowPage[] = document.pages.map((original) => {
    let page = original;
    if (seenPageIds.has(page.id)) {
      changed = true;
      let suffix = 2;
      while (pageIds.has(`${original.id}-${suffix}`)) suffix += 1;
      page = { ...page, id: `${original.id}-${suffix}` };
      pageIds.add(page.id);
    }
    seenPageIds.add(page.id);
    const blocks = page.blocks.flatMap((block): ReportFlowPage['blocks'] => {
      const all = blockItems(block);
      const items = all.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      if (items.length === all.length && isSoundBlock(block)) return [block];
      changed = true;
      return toRows(items);
    });
    return blocks.length === page.blocks.length &&
      blocks.every((block, index) => block === page.blocks[index])
      ? page
      : { ...page, blocks };
  });
  if (pages.length === 0) {
    changed = true;
    pages.push({ id: FALLBACK_PAGE_ID, blocks: [] });
  }
  if (!changed) return document;
  const repaired = { ...document, pages };
  if (stage === 'load') {
    if (reportedLoadRepairs.has(document.id)) return repaired;
    reportedLoadRepairs.add(document.id);
  }

  const violations = validateReportDocumentLayout(document.pages).map(
    (violation) => violation.code
  );
  console.warn(
    `[report-document] repaired the layout of '${document.id}' on ${stage}:`,
    violations.join(', ')
  );
  emitMixpanelUserEvent(
    BuildADashTrackingEvents.REPORT_DOCUMENT_LAYOUT_REPAIRED,
    { documentId: document.id, stage, violations: violations.join(',') }
  );
  return repaired;
};
